import React, { useState } from "react";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { Panel, EmptyState, Field, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

// Generic CRUD table + modal form driven by a field/column schema.
export default function DataModule({ apiPath, queryKey, columns, fields, canWrite = true, icon, emptyTitle, emptyHint, addLabel = "Add", testid = "item", canAdd = true }) {
  const invalidate = useInvalidate();
  const { data: rows = [], isLoading } = useList(queryKey, `/${apiPath}`);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const defaults = () => Object.fromEntries(fields.map((f) => [f.key, f.default ?? (f.type === "number" ? 0 : "")]));
  const openNew = () => { setForm(defaults()); setEditing(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...defaults(), ...r }); setEditing(r.id); setOpen(true); };

  const save = async () => {
    const required = fields.filter((f) => f.required && !String(form[f.key] ?? "").trim());
    if (required.length) { toast.error(`${required[0].label} is required`); return; }
    setSaving(true);
    try {
      const payload = {};
      fields.forEach((f) => { payload[f.key] = f.type === "number" ? Number(form[f.key]) || 0 : form[f.key]; });
      if (editing) await api.put(`/${apiPath}/${editing}`, payload);
      else await api.post(`/${apiPath}`, payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      invalidate([queryKey, "dashboard"]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this record?")) return; await api.delete(`/${apiPath}/${id}`); invalidate([queryKey, "dashboard"]); toast.success("Deleted"); };

  return (
    <div>
      {canWrite && (
        <div className="flex justify-end mb-3">
          {canAdd && <Button data-testid={`new-${testid}-btn`} onClick={openNew} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold"><Plus size={18} weight="bold" className="mr-1.5" />{addLabel}</Button>}
        </div>
      )}
      <Panel>
        {isLoading ? <div className="p-6 text-slate-400">Loading…</div> : rows.length === 0 ? (
          <EmptyState icon={icon} title={emptyTitle} hint={emptyHint} action={canWrite && canAdd && <Button onClick={openNew} className="bg-blue-600 text-white hover:bg-blue-700"><Plus size={16} className="mr-1" />{addLabel}</Button>} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  {columns.map((c) => <TableHead key={c.key} className={c.hideSm ? "hidden md:table-cell" : ""}>{c.label}</TableHead>)}
                  {canWrite && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="border-slate-100 hover:bg-slate-50" data-testid={`${testid}-row-${r.id}`}>
                    {columns.map((c) => (
                      <TableCell key={c.key} className={c.hideSm ? "hidden md:table-cell" : ""}>
                        {c.key === "status" ? <StatusBadge status={r[c.key]} />
                          : c.render ? c.render(r)
                          : c.mono ? <span className="font-mono text-sm text-slate-700">{r[c.key] || "—"}</span>
                          : <span className="text-sm text-slate-700">{r[c.key] || "—"}</span>}
                      </TableCell>
                    ))}
                    {canWrite && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => openEdit(r)} className="p-1.5 text-slate-400 hover:text-blue-600" data-testid={`edit-${testid}-${r.id}`}><PencilSimple size={16} /></button>
                          <button onClick={() => remove(r.id)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`delete-${testid}-${r.id}`}><Trash size={16} /></button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit" : addLabel}</DialogTitle>
            <DialogDescription className="text-slate-500">Fill in the details below.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                <Field label={f.label + (f.required ? " *" : "")}>
                  {f.type === "textarea" ? (
                    <Textarea value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="bg-white border-slate-300" rows={3} />
                  ) : f.type === "select" ? (
                    <Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                      <SelectTrigger className="bg-white border-slate-300" data-testid={`${testid}-${f.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o} className="capitalize">{o.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                      value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      className="bg-white border-slate-300 focus:border-blue-500 focus-visible:ring-blue-500" data-testid={`${testid}-${f.key}`} />
                  )}
                </Field>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50">Cancel</Button>
            <Button data-testid={`save-${testid}-btn`} onClick={save} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
